import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { saveQuoteToFirestore } from '../services/applicationService';
import { 
  FiSend, 
  FiArrowLeft,
  FiArrowRight,
  FiMail,
  FiPhone,
  FiCheckCircle,
  FiClipboard,
  FiMapPin,
  FiBriefcase,
  FiUsers
} from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import Chatbot from '../components/Chatbot';
import '../styles/pages/RequestQuote.css';
import '../styles/animations/ScrollAnimations.css';

// Branch data organized by region
const branchData = {
  Luzon: [
    { id: 'luzon-main', name: 'Luzon Branch (Mandaluyong)', address: '68 EDSA cor Pinatubo St. Barangay Malamig, Mandaluyong City', manager: 'Frosie Adecer' }
  ],
  Visayas: [
    { id: 'bacolod', name: 'Bacolod Branch', address: 'Mackinly Baybay St. cor. Sto. Niño Brgy. Mambulac, Silay City, Negros Occidental', manager: 'Rudyrick John Perez' },
    { id: 'cebu', name: 'Cebu Branch', address: '6th St, Sitio Kana-as, Guadalajara, Guadalupe Cebu City', manager: 'Rudyrick John Perez' },
    { id: 'dumaguete', name: 'Dumaguete Branch', address: 'South Bacacay, Dumaguete City', manager: 'Rudyrick John Perez' },
    { id: 'iloilo', name: 'Iloilo Branch', address: '206 MH Del Pilar St. Molo, Iloilo City', manager: 'Rudyrick John Perez' },
    { id: 'bohol', name: 'Bohol Branch', address: 'Felis Garbiela Bldg. Castillo - Facifico Road Barangay Dao, Tagbilaran Bohol', manager: 'Rudyrick John Perez' },
    { id: 'tacloban', name: 'Leyte / Tacloban Branch', address: 'Palo, Alang-Alang Marasbaras, Tacloban City', manager: 'Dave Cueme' },
    { id: 'southern-leyte', name: 'Southern Leyte Branch', address: 'P. Gonzales St. Abgao, Maasin City', manager: 'Dave Cueme' },
    { id: 'ormoc', name: 'Ormoc Leyte Branch', address: 'District 21 San Vedal and Real St. Ormoc City', manager: 'Dave Cueme' },
    { id: 'samar', name: 'Samar Branch', address: 'Eastwest Bank Brgy. 5 Curry Avenue, Corner San Bartolome St. Catbalogan City', manager: 'Dave Cueme' }
  ],
  Mindanao: [
    { id: 'davao', name: 'Head Office - Davao', address: '3rd Floor Marcela Building, Palma Gil Street, Barangay 34-D, Davao City', manager: 'Myra Betonio' },
    { id: 'barm', name: 'BARM Branch', address: 'Door 4, Enilo Apartment RH-4 Sinsuat', manager: 'Dave Cueme' },
    { id: 'butuan', name: 'Butuan Branch', address: 'Dr. 3 Sevilla Apt. Nonan Village Barangay Bayanihan JC Aquino Avenue, Butuan City', manager: 'Reymart Ligue' },
    { id: 'cdo', name: 'Cagayan de Oro Branch', address: 'Bontong, Camaman-an Cagayan de Oro', manager: 'Reymart Ligue' },
    { id: 'dipolog', name: 'Dipolog Branch', address: 'Dipolog City', manager: 'Rudyrick John Perez' },
    { id: 'gensan', name: 'General Santos Branch', address: 'No. 55 Manga Street, Barangay Dadiangas North, General Santos City', manager: 'Dave Cueme' },
    { id: 'malaybalay', name: 'Malaybalay Branch', address: 'Malaybalay Water District/Primewater Sayre Highway, Malaybalay, Bukidnon', manager: 'Reymart Ligue' },
    { id: 'misamis', name: 'Misamis Branch', address: 'Bypass Road, Villanueva Misamis Oriental', manager: 'Reymart Ligue' },
    { id: 'ozamiz', name: 'Ozamiz Branch', address: 'Purok2, Badong Exec. Village Barangay Carangan, Ozamiz City', manager: 'Rudyrick John Perez' },
    { id: 'pagadian', name: 'Pagadian Branch', address: 'Purok Kamansi, Lenienza Pagadian City', manager: 'Rudyrick John Perez' },
    { id: 'surigao', name: 'Surigao Branch', address: 'San Juan Lipata Road 7, Barangay Sabang, Surigao City', manager: 'Dave Cueme' },
    { id: 'valencia', name: 'Valencia Branch', address: 'Purok-13-A Pobalcion Hagkol, Valencia City Bukidnon', manager: 'Reymart Ligue' },
    { id: 'zamboanga', name: 'Zamboanga Branch', address: 'Zamboanga City', manager: 'Rudyrick John Perez' }
  ]
};

const addressOptionsByRegion = {
  Luzon: [
    { city: 'Mandaluyong City', province: 'Metro Manila' },
    { city: 'Manila City', province: 'Metro Manila' },
    { city: 'Quezon City', province: 'Metro Manila' },
    { city: 'Makati City', province: 'Metro Manila' },
    { city: 'Baguio City', province: 'Benguet' },
    { city: 'Legazpi City', province: 'Albay' }
  ],
  Visayas: [
    { city: 'Bacolod City', province: 'Negros Occidental' },
    { city: 'Cebu City', province: 'Cebu' },
    { city: 'Dumaguete City', province: 'Negros Oriental' },
    { city: 'Iloilo City', province: 'Iloilo' },
    { city: 'Tagbilaran City', province: 'Bohol' },
    { city: 'Tacloban City', province: 'Leyte' },
    { city: 'Catbalogan City', province: 'Samar' },
    { city: 'Maasin City', province: 'Southern Leyte' },
    { city: 'Ormoc City', province: 'Leyte' },
    { city: 'Silay City', province: 'Negros Occidental' }
  ],
  Mindanao: [
    { city: 'Davao City', province: 'Davao del Sur' },
    { city: 'Cotabato City', province: 'Maguindanao del Norte' },
    { city: 'Butuan City', province: 'Agusan del Norte' },
    { city: 'Cagayan de Oro City', province: 'Misamis Oriental' },
    { city: 'Dipolog City', province: 'Zamboanga del Norte' },
    { city: 'General Santos City', province: 'South Cotabato' },
    { city: 'Malaybalay City', province: 'Bukidnon' },
    { city: 'Villanueva', province: 'Misamis Oriental' },
    { city: 'Ozamiz City', province: 'Misamis Occidental' },
    { city: 'Pagadian City', province: 'Zamboanga del Sur' },
    { city: 'Surigao City', province: 'Surigao del Norte' },
    { city: 'Valencia City', province: 'Bukidnon' },
    { city: 'Zamboanga City', province: 'Zamboanga del Sur' }
  ]
};

const RequestQuote = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const preselectedService = location.state?.service || '';

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [slideDirection, setSlideDirection] = useState('next');

  const [formData, setFormData] = useState({
    // Step 1
    companyName: '',
    serviceNeeded: preselectedService,
    region: '',
    // Step 2
    branch: '',
    // Step 3
    contactPerson: '',
    position: '',
    email: '',
    phone: '',
    companyAddress: '',
    companyCityTown: '',
    companyRegion: '',
    companyPostalCode: '',
    numberOfWorkers: ''
  });

  useEffect(() => {
    if (preselectedService) {
      setFormData(prev => ({ ...prev, serviceNeeded: preselectedService }));
    }
  }, [preselectedService]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const elements = document.querySelectorAll('[class*="scroll-"]');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let nextValue = value;
    if (name === 'phone') {
      nextValue = value.replace(/\D/g, '').slice(0, 11);
    } else if (name === 'companyPostalCode') {
      nextValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setFormData(prev => {
      const nextForm = { ...prev, [name]: nextValue };
      if (name === 'region') {
        nextForm.branch = '';
        nextForm.companyCityTown = '';
        nextForm.companyRegion = '';
        nextForm.companyPostalCode = '';
      }
      if (name === 'companyCityTown') {
        nextForm.companyRegion = '';
        nextForm.companyPostalCode = '';
      }
      return nextForm;
    });
  };

  const selectBranch = (branchId) => {
    setFormData(prev => ({ ...prev, branch: branchId }));
  };

  const validateStep = (step) => {
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const hasValidPostalCode = /^\d{4}$/.test(formData.companyPostalCode.trim());
    const hasRequiredAddressDetails =
      formData.companyAddress &&
      formData.companyCityTown &&
      formData.companyRegion &&
      hasValidPostalCode;
    const normalizedEmail = formData.email.trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
    const workerCount = Number(formData.numberOfWorkers);
    const hasValidWorkerCount = Number.isInteger(workerCount) && workerCount > 0;

    switch (step) {
      case 1: return formData.companyName && formData.serviceNeeded && formData.region;
      case 2: return formData.branch;
      case 3: return formData.contactPerson && isValidEmail && phoneDigits.length === 11 && hasValidWorkerCount && hasRequiredAddressDetails;
      default: return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < 3) {
      setSlideDirection('next');
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setSlideDirection('prev');
      setCurrentStep(prev => prev - 1);
    }
  };

  const getSelectedBranch = () => {
    if (!formData.region || !formData.branch) return null;
    const branches = branchData[formData.region] || [];
    return branches.find(b => b.id === formData.branch);
  };

  const availableCityOptions = Array.from(
    new Set((addressOptionsByRegion[formData.region] || []).map((option) => option.city))
  );

  const availableProvinceOptions = formData.companyCityTown
    ? Array.from(
        new Set(
          (addressOptionsByRegion[formData.region] || [])
            .filter((option) => option.city === formData.companyCityTown)
            .map((option) => option.province)
        )
      )
    : [];

  const formatAddress = (address, cityTown, region, postalCode) =>
    [address, cityTown, region, postalCode]
      .map((part) => (typeof part === 'string' ? part.trim() : part))
      .filter(Boolean)
      .join(', ');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!e.currentTarget.checkValidity()) {
      e.currentTarget.reportValidity();
      return;
    }
    if (!validateStep(3)) return;
    setIsSubmitting(true);
    setSubmitStatus(null);

    const selectedBranch = getSelectedBranch();
    const companyAddress = formatAddress(
      formData.companyAddress,
      formData.companyCityTown,
      formData.companyRegion,
      formData.companyPostalCode
    );
    try {
      await saveQuoteToFirestore({
        companyName: formData.companyName,
        serviceNeeded: formData.serviceNeeded,
        region: formData.region,
        branchId: formData.branch,
        branchName: selectedBranch?.name || 'N/A',
        branchAddress: selectedBranch?.address || 'N/A',
        contactPerson: formData.contactPerson,
        position: formData.position || 'Not specified',
        email: formData.email,
        phone: formData.phone,
        companyAddress: companyAddress || 'Not provided',
        numberOfWorkers: formData.numberOfWorkers,
      });
      setSubmitStatus('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Quote submission error:', error);
      setSubmitStatus('error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success State
  if (submitStatus === 'success') {
    const selectedBranch = getSelectedBranch();
    return (
      <div className="quote-page">
        <section className="quote-page-title">
          <div className="container">
            <h1>Request a Quote</h1>
            <p>MEDCoop - Manpower Service Quotation</p>
          </div>
        </section>
        <section className="quote-success-section">
          <div className="container">
            <div className="quote-success-card">
              <div className="success-icon-large">
                <FiCheckCircle />
              </div>
              <h2>Quote Request Submitted!</h2>
              <p>Thank you, <strong>{formData.contactPerson}</strong> from <strong>{formData.companyName}</strong>.</p>
              <p>Your request for <strong>{formData.numberOfWorkers} {formData.serviceNeeded} worker(s)</strong> has been sent to <strong>{selectedBranch?.name || formData.region}</strong>.</p>
              <div className="success-details">
                <div className="success-detail-item">
                  <FiMail />
                  <span>Confirmation will be sent to <strong>{formData.email}</strong></span>
                </div>
                <div className="success-detail-item">
                  <FiPhone />
                  <span>Our team will contact you within <strong>24-48 business hours</strong></span>
                </div>
                <div className="success-detail-item">
                  <FiClipboard />
                  <span>Reference #: <strong>QR-{Date.now().toString().slice(-8)}</strong></span>
                </div>
              </div>
              <div className="success-actions">
                <Link to="/services" className="btn btn-primary">
                  <FiArrowLeft /> Back to Services
                </Link>
                <Link to="/" className="btn btn-outline">
                  Go to Home
                </Link>
              </div>
            </div>
          </div>
        </section>
        <Chatbot />
      </div>
    );
  }

  const availableBranches = branchData[formData.region] || [];

  return (
    <div className="quote-page">
      {/* Page Title */}
      <section className="quote-page-title">
        <div className="container">
          <h1>Request a Quote</h1>
          <p>MEDCoop - Manpower Service Quotation</p>
        </div>
      </section>

      {/* Step Progress */}
      <div className="quote-progress-bar">
        <div className="container">
          <div className="progress-steps">
            {/* Background line */}
            <div className="progress-track">
              <div className="progress-track-fill" style={{ width: currentStep === 1 ? '0%' : currentStep === 2 ? '50%' : '100%' }} />
            </div>
            {/* Steps */}
            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
              <div className="progress-dot">{currentStep > 1 ? '✓' : '1'}</div>
              <span>Company & Service</span>
            </div>
            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
              <div className="progress-dot">{currentStep > 2 ? '✓' : '2'}</div>
              <span>Select Branch</span>
            </div>
            <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="progress-dot">3</div>
              <span>Details & Submit</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <section className="quote-form-section">
        <div className="container">
          <div className="quote-form-card">
            {submitStatus === 'error' && (
              <div
                role="alert"
                style={{
                  marginBottom: '1rem',
                  padding: '0.875rem 1rem',
                  border: '1px solid #d73a49',
                  borderRadius: '0.625rem',
                  backgroundColor: '#fff5f5',
                  color: '#7f1d1d'
                }}
              >
                We couldn't send your request automatically. Please review your details and try again, or use the email draft that opened in a new tab.
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className={`quote-step-container slide-${slideDirection}`}>

                {/* STEP 1: Company, Service, Region */}
                {currentStep === 1 && (
                  <div className="quote-step" key="step1">
                    <div className="step-icon-header">
                      <div className="step-icon-circle"><FiBriefcase /></div>
                      <div>
                        <h2>Company & Service</h2>
                        <p>Tell us your company name, the manpower you need, and your area.</p>
                      </div>
                    </div>
                    <div className="quote-form-fields">
                      <div className="form-group">
                        <label htmlFor="companyName">Company Name *</label>
                        <input
                          id="companyName"
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          placeholder="e.g. ABC Corporation"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="serviceNeeded">Type of Manpower Needed *</label>
                        <select
                          id="serviceNeeded"
                          name="serviceNeeded"
                          value={formData.serviceNeeded}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select a Service</option>
                          <option value="Janitorial Services">Janitorial Services</option>
                          <option value="Messengers/Collectors">Messengers / Collectors</option>
                          <option value="Company Drivers">Company Drivers</option>
                          <option value="Construction Workers">Construction Workers</option>
                          <option value="Promotion Merchandisers">Promotion Merchandisers</option>
                          <option value="CCTV Operators">CCTV Operators</option>
                          <option value="Radio Operators">Radio Operators</option>
                          <option value="Carpet Shampooing">Carpet Shampooing</option>
                          <option value="Office Temp Clerks">Office Temp Clerks</option>
                          <option value="Cash Sorters">Cash Sorters</option>
                          <option value="Concierge Services">Concierge Services</option>
                          <option value="Other Allied Services">Other Allied Services</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="region">Where Are You Located? *</label>
                        <select
                          id="region"
                          name="region"
                          value={formData.region}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Region</option>
                          <option value="Luzon">Luzon</option>
                          <option value="Visayas">Visayas</option>
                          <option value="Mindanao">Mindanao</option>
                        </select>
                      </div>
                    </div>
                    <div className="step-actions">
                      <div></div>
                      <button type="button" className="btn btn-primary" onClick={nextStep} disabled={!validateStep(1)}>
                        Next: Select Branch <FiArrowRight />
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 2: Branch Selection */}
                {currentStep === 2 && (
                  <div className="quote-step" key="step2">
                    <div className="step-icon-header">
                      <div className="step-icon-circle"><FiMapPin /></div>
                      <div>
                        <h2>Select Nearest Branch</h2>
                        <p>Choose the MEDCOOP branch closest to your deployment area in <strong>{formData.region}</strong>.</p>
                      </div>
                    </div>
                    <div className="branches-list" role="radiogroup" aria-label="Available MEDCOOP branches">
                      {availableBranches.map((branch, index) => {
                        const isSelected = formData.branch === branch.id;
                        return (
                          <div
                            key={branch.id}
                            id={`quote-branch-${branch.id}`}
                            className={`branch-item ${isSelected ? 'selected' : ''}`}
                            role="radio"
                            aria-checked={isSelected}
                            tabIndex={isSelected || (!formData.branch && index === 0) ? 0 : -1}
                            onClick={() => selectBranch(branch.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                selectBranch(branch.id);
                                return;
                              }

                              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                                event.preventDefault();
                                const nextIndex = (index + 1) % availableBranches.length;
                                const nextBranch = availableBranches[nextIndex];
                                selectBranch(nextBranch.id);
                                requestAnimationFrame(() => {
                                  document.getElementById(`quote-branch-${nextBranch.id}`)?.focus();
                                });
                                return;
                              }

                              if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                                event.preventDefault();
                                const previousIndex = (index - 1 + availableBranches.length) % availableBranches.length;
                                const previousBranch = availableBranches[previousIndex];
                                selectBranch(previousBranch.id);
                                requestAnimationFrame(() => {
                                  document.getElementById(`quote-branch-${previousBranch.id}`)?.focus();
                                });
                              }
                            }}
                          >
                            <div className="branch-radio">
                              <div className="branch-radio-dot" />
                            </div>
                            <div className="branch-info">
                              <h4>{branch.name}</h4>
                              <p>{branch.address}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="step-actions">
                      <button type="button" className="btn btn-back" onClick={prevStep}>
                        <FiArrowLeft /> Back
                      </button>
                      <button type="button" className="btn btn-primary" onClick={nextStep} disabled={!validateStep(2)}>
                        Next: Your Details <FiArrowRight />
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 3: Contact Info & Worker Details */}
                {currentStep === 3 && (
                  <div className="quote-step" key="step3">
                    <div className="step-icon-header">
                      <div className="step-icon-circle"><FiUsers /></div>
                      <div>
                        <h2>Your Details</h2>
                        <p>Provide your contact information and manpower requirements.</p>
                      </div>
                    </div>
                    <div className="quote-form-fields two-col">
                      <div className="form-group">
                        <label htmlFor="contactPerson">Contact Person *</label>
                        <input
                          id="contactPerson"
                          type="text"
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleInputChange}
                          placeholder="e.g. Juan Dela Cruz"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="position">Position / Title</label>
                        <input
                          id="position"
                          type="text"
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                          placeholder="e.g. HR Manager"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          id="email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="e.g. juan@company.com"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                          id="phone"
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="e.g. 0917-123-4567"
                          inputMode="numeric"
                          maxLength={11}
                          pattern="[0-9]{11}"
                          title="Phone number must be exactly 11 digits"
                          required
                        />
                      </div>
                      <div className="form-group full-span">
                        <h3 style={{ margin: 0, fontWeight: 600, fontSize: '1rem', color: '#0f172a' }}>Company Address</h3>
                      </div>
                      <div className="form-group full-span">
                        <label htmlFor="companyAddress">Company Address *</label>
                        <input
                          id="companyAddress"
                          type="text"
                          name="companyAddress"
                          value={formData.companyAddress}
                          onChange={handleInputChange}
                          placeholder="Include house number, building/unit, street, and barangay (e.g. Unit 1201, 12/F XYZ Building, J. Rizal St., Barangay San Roque)"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="companyCityTown">City / Town *</label>
                        <select
                          id="companyCityTown"
                          name="companyCityTown"
                          value={formData.companyCityTown}
                          onChange={handleInputChange}
                          disabled={!formData.region}
                          required
                        >
                          <option value="">{formData.region ? 'Select City / Town' : 'Select Region First'}</option>
                          {availableCityOptions.map((city) => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="companyRegion">Province *</label>
                        <select
                          id="companyRegion"
                          name="companyRegion"
                          value={formData.companyRegion}
                          onChange={handleInputChange}
                          disabled={!formData.companyCityTown}
                          required
                        >
                          <option value="">{formData.companyCityTown ? 'Select Province' : 'Select City First'}</option>
                          {availableProvinceOptions.map((province) => (
                            <option key={province} value={province}>{province}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group full-span">
                        <label htmlFor="companyPostalCode">ZIP / Postal Code *</label>
                        <input
                          id="companyPostalCode"
                          type="text"
                          name="companyPostalCode"
                          value={formData.companyPostalCode}
                          onChange={handleInputChange}
                          inputMode="numeric"
                          maxLength={4}
                          pattern="[0-9]{4}"
                          title="Postal code must be exactly 4 digits"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-divider"><span>Deployment Requirements</span></div>

                    <div className="quote-form-fields two-col">
                      <div className="form-group full-span">
                        <label htmlFor="numberOfWorkers">Number of Workers Needed *</label>
                        <input
                          id="numberOfWorkers"
                          type="number"
                          name="numberOfWorkers"
                          value={formData.numberOfWorkers}
                          onChange={handleInputChange}
                          placeholder="e.g. 10"
                          min="1"
                          step="1"
                          required
                        />
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="quote-summary-strip">
                      <div className="summary-item"><FiBriefcase /> <span><strong>{formData.companyName}</strong></span></div>
                      <div className="summary-item"><FiClipboard /> <span>{formData.serviceNeeded}</span></div>
                      <div className="summary-item"><FiMapPin /> <span>{getSelectedBranch()?.name}</span></div>
                    </div>

                    <div className="step-actions">
                      <button type="button" className="btn btn-back" onClick={prevStep}>
                        <FiArrowLeft /> Back
                      </button>
                      <button type="submit" className="btn btn-primary btn-submit-quote" disabled={isSubmitting || !validateStep(3)}>
                        {isSubmitting ? 'Submitting...' : 'Submit Quote Request'} <FiSend />
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </form>
          </div>
        </div>
      </section>

      <Chatbot />
    </div>
  );
};

export default RequestQuote;
